import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import ListingCard from './ListingCard';
import ForSaleListings from './ForSaleListings';
import RentalFilterPanel, { DEFAULT_RENTAL_FILTERS, rentalBedroomCategory, rentalViewType } from './RentalFilterPanel';
import { useApi } from '../services/api';
import { weekNumberForDate } from '../utils/forSale';

// ── Available Stays tab: same filter-panel UX as the For Sale tab ───────────
function AvailableStaysTab({ propertyId, rentalListings }) {
  const { data: descData } = useApi(`/properties/${propertyId}/room-descriptions`);
  const descriptionsByUnit = useMemo(() => descData?.byUnit || {}, [descData]);

  const [filters, setFilters] = useState(DEFAULT_RENTAL_FILTERS);

  const filtered = useMemo(() => {
    let rows = rentalListings.slice();
    if (filters.bedroom)  rows = rows.filter(l => rentalBedroomCategory(l, descriptionsByUnit) === filters.bedroom);
    if (filters.viewType) rows = rows.filter(l => rentalViewType(l, descriptionsByUnit).toLowerCase().includes(filters.viewType));
    if (filters.priceMin) rows = rows.filter(l => l.current_price >= parseFloat(filters.priceMin) * 100);
    if (filters.priceMax) rows = rows.filter(l => l.current_price <= parseFloat(filters.priceMax) * 100);
    if (filters.guests)   rows = rows.filter(l => l.max_guests >= parseInt(filters.guests, 10));
    if (filters.weekFrom || filters.weekTo) {
      const from = filters.weekFrom ? parseInt(filters.weekFrom, 10) : 1;
      const to = filters.weekTo ? parseInt(filters.weekTo, 10) : 52;
      rows = rows.filter(l => {
        const { week } = weekNumberForDate(l.check_in_date);
        return week >= from && week <= to;
      });
    }
    rows.sort((a, b) => {
      if (filters.sortBy === 'price_asc')  return a.current_price - b.current_price;
      if (filters.sortBy === 'price_desc') return b.current_price - a.current_price;
      if (filters.sortBy === 'discount_desc') {
        const discA = a.ceiling_price ? (a.ceiling_price - a.current_price) / a.ceiling_price : 0;
        const discB = b.ceiling_price ? (b.ceiling_price - b.current_price) / b.ceiling_price : 0;
        return discB - discA;
      }
      return new Date(a.check_in_date) - new Date(b.check_in_date); // 'checkin'
    });
    return rows;
  }, [rentalListings, descriptionsByUnit, filters]);

  if (rentalListings.length === 0) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', background: 'var(--surface-alt)', borderRadius: 'var(--radius-md)', color: 'var(--ink-muted)' }}>
        <p style={{ marginBottom: '0.5rem' }}>No active listings right now.</p>
        <p style={{ fontSize: 'var(--text-sm)' }}>Own time here? <Link to="/register?role=owner">List your stay →</Link></p>
      </div>
    );
  }

  return (
    <div>
      <RentalFilterPanel
        filters={filters}
        onChange={setFilters}
        listings={rentalListings}
        descriptionsByUnit={descriptionsByUnit}
        resultCount={filtered.length}
      />

      {filtered.length === 0 ? (
        <div style={{ padding: '2rem', textAlign: 'center', background: 'var(--surface-alt)', borderRadius: 'var(--radius-md)', color: 'var(--ink-faint)' }}>
          No stays match your filters.
        </div>
      ) : (
        <div className="listing-list-vertical">
          {filtered.map(l => (
            <ListingCard key={l.id} listing={l} horizontal roomType={descriptionsByUnit[l.unit_number]?.label} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Available Stays / For Sale tabs, sharing one fixed-height scroll area ────
// Keeps the property map above always in view while the user scrolls through
// listings, instead of the page growing indefinitely.
export default function PropertyListingsTabs({ propertyId, propertyName, rentalListings }) {
  const [tab, setTab] = useState('stays');

  return (
    <div>
      <div className="pill-tabs">
        <button className={`pill-tab-btn ${tab === 'stays' ? 'active' : ''}`} onClick={() => setTab('stays')}>
          Available Stays
          {rentalListings.length > 0 && (
            <span className="pill-tab-count">{rentalListings.length}</span>
          )}
        </button>
        <button className={`pill-tab-btn ${tab === 'forsale' ? 'active' : ''}`} onClick={() => setTab('forsale')}>
          For Sale
        </button>
      </div>

      <div className="property-listings-scroll">
        {tab === 'stays' && (
          <AvailableStaysTab propertyId={propertyId} rentalListings={rentalListings} />
        )}

        {tab === 'forsale' && (
          <ForSaleListings propertyId={propertyId} propertyName={propertyName} />
        )}
      </div>
    </div>
  );
}
